import { iatan2, imax, withinRange } from '../math/fixed.ts';
import type { BattleState, Hero } from '../types.ts';
import { damageEnemy, ev, isFlying } from './combat.ts';

/**
 * The five strikes (Las vías 3, docs/SAGAS.md): the Deep Anvil is the node, and it beats. A ring of light closes on it
 * again and again. A hero by the Anvil who strikes it as the ring closes sends a wave of heat through the hall that
 * burns and stuns every nightmare in it; then the Anvil rests a while before it beats again, and the next ring
 * closes quicker. A strike off the beat (or while it rests) is lost, and that
 * hero's hammer is up a moment. Five on the beat set the vein alight and turn whatever is left to ash: won. The Anvil
 * falling loses it.
 */

export const anvilTick = (s: BattleState): void => {
  const r = s.rules?.anvil;
  if (!r) return;
  const st = (s.anvil ??= { open: r.first - r.ring[0], close: r.first, strikes: 0, done: 0, up: {} });
  if (!s.started || st.done) return;
  // Closed and gone by with no strike on it: the next one, as slow as that one.
  if (s.waveT > st.close + r.window) {
    const len = st.close - st.open;
    st.open = st.close;
    st.close += len;
  }
};

/** How long a ring takes to close after `n` strikes on the beat. */
const ringOf = (s: BattleState, n: number): number => {
  const r = s.rules?.anvil;
  return r ? imax(r.ring[1], r.ring[0] - n * r.step) : 0;
};

/** The hammer command: by the Anvil, on the beat or not. */
export const anvilStrike = (s: BattleState, h: Hero): void => {
  const r = s.rules?.anvil;
  const st = s.anvil;
  if (!r || !st || !s.started || st.done || h.downTicks > 0) return;
  if ((st.up[h.pid] ?? 0) > s.tick || !withinRange(h.x, h.y, 0, 0, r.reach)) return;
  h.facing = iatan2(-h.y, -h.x);
  if (s.waveT < st.open || s.waveT < st.close - r.window || s.waveT > st.close + r.window) {
    st.up[h.pid] = s.tick + r.miss;
    ev(s, { t: 'anvil', kind: 'miss', n: st.strikes, x: h.x, y: h.y });
    return;
  }
  st.strikes++;
  const fifth = st.strikes >= r.need;
  for (const e of s.enemies) {
    if (e.hp <= 0 || e.kind === 'boneWall' || isFlying(e) || !withinRange(e.x, e.y, 0, 0, r.radius)) continue;
    if (fifth) e.hp = 0;
    else {
      damageEnemy(s, e, r.foe, { pid: h.pid, ignoresArmor: true, noReact: true });
      e.stun = imax(e.stun, r.stun);
    }
  }
  if (fifth) {
    // The vein takes the heat: the whole hall goes red, and what was left in it is ash (the flyers too).
    for (const e of s.enemies) if (e.hp > 0 && e.kind !== 'boneWall') e.hp = 0;
    st.done = s.tick;
    ev(s, { t: 'anvil', kind: 'fifth', n: st.strikes, x: 0, y: 0 });
    return;
  }
  ev(s, { t: 'anvil', kind: 'hit', n: st.strikes, x: 0, y: 0 });
  st.open = s.waveT + r.rest;
  st.close = st.open + ringOf(s, st.strikes);
};

/** Five on the beat: won (a moment later); the Anvil falling: lost. */
export const anvilResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.anvil;
  if (!r) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.anvil?.done && s.tick - s.anvil.done >= r.beat ? 'won' : null;
};
